import { useState, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  BookOpen,
  Save,
  X,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_blogs,
  save_update_blog,
  delete_website_master_data,
} from "../ServiceConnection/serviceconnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
// Backend AdminBlog model:
// primary_id, image_url (hero), image_url1 (card), image_url2, image_url3,
// title_name, tag_line, author, author_image, author_position, author_bio,
// keyword, keyword_id, read_minit, description, description2, description3,
// page_url_data, category_id, category_slug, flag
//
// get_all_blogs response:
// data_blog, data_blog_category, data_blog_keyword,
// data_blog_faq, data_blog_faq_id, blog_image

const ITEMS_PER_PAGE = 5;

const EMPTY_FORM = {
  primary_id: "0",
  title_name: "",
  tag_line: "",
  author: "",
  author_position: "",
  author_bio: "",
  read_minit: "",
  page_url_data: "",
  keyword_id: "",
  otherKeyword: "",
  category_id: "0",
  // existing image URLs (for edit mode)
  image_url: "",
  image_url1: "",
  image_url2: "",
  image_url3: "",
  author_image: "",
};

// ─── Quill toolbar config ─────────────────────────────────────────────────────
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link", "image", "blockquote"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "blockquote",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// ─── Rich Editor ──────────────────────────────────────────────────────────────
const RichEditor = ({ value, onChange, placeholder = "Start writing..." }) => (
  <div className="rich-editor-wrapper">
    <ReactQuill
      theme="snow"
      value={value}
      onChange={onChange}
      modules={QUILL_MODULES}
      formats={QUILL_FORMATS}
      placeholder={placeholder}
      style={{ height: "220px", marginBottom: "42px" }}
    />
  </div>
);

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ blog, onConfirm, onClose, deleteLoader }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <Trash2 className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="mb-2 text-base font-bold text-foreground">
          Delete Blog Post?
        </h3>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-foreground">
            "{blog?.title_name}"
          </span>
          ? This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 border-t border-border p-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          onClick={onClose}
          disabled={deleteLoader}
        >
          Cancel
        </Button>
        <Button
          disabled={deleteLoader}
          className="flex-1 gap-2 rounded-xl bg-destructive text-white hover:bg-destructive/90"
          onClick={onConfirm}
        >
          {deleteLoader ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Yes, Delete
        </Button>
      </div>
    </div>
  </div>
);

// ─── Image Upload Field ───────────────────────────────────────────────────────
const ImageUploadField = ({
  id,
  name,
  label,
  hint,
  file,
  onChange,
  oldUrl,
}) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold text-foreground">
      {label}
    </label>
    <label
      htmlFor={id}
      className="flex h-10 w-full cursor-pointer items-center justify-between rounded-lg border border-input bg-background px-3 text-sm transition-colors hover:bg-accent"
    >
      <span className={file ? "text-foreground" : "text-muted-foreground"}>
        {file
          ? file.name
          : oldUrl
            ? "Image uploaded — click to replace"
            : `Choose ${label}`}
      </span>
      <ImagePlus className="h-4 w-4 shrink-0 text-muted-foreground" />
    </label>
    <input
      type="file"
      id={id}
      name={name}
      onChange={onChange}
      className="hidden"
      accept="image/*"
    />
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    {oldUrl && !file && (
      <div className="mt-2 h-16 w-24 overflow-hidden rounded-lg border border-border">
        <img
          src={oldUrl}
          alt="current"
          className="h-full w-full object-cover"
        />
      </div>
    )}
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const Blogs = () => {
  const { toast } = useToast();

  const [blogs, setBlogs] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [categories, setCategories] = useState([]); // ← data_blog_category
  const [allFaqs, setAllFaqs] = useState([]);
  const [blogImageBase, setBlogImageBase] = useState("");
  const [formVisible, setFormVisible] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editorData, setEditorData] = useState(""); // description
  const [editorData2, setEditorData2] = useState(""); // description2
  const [editorData3, setEditorData3] = useState(""); // description3
  // doctor_image_blog1 → image_url1 (card thumbnail)
  const [selectedFileBlog, setSelectedFileBlog] = useState(null);
  // doctor_image_blog  → image_url  (hero)
  const [selectedFileBlog2, setSelectedFileBlog2] = useState(null);
  // doctor_image_blog2 → image_url2
  const [selectedFileBlog4, setSelectedFileBlog4] = useState(null);
  // doctor_image_blog3 → image_url3
  const [selectedFileBlog5, setSelectedFileBlog5] = useState(null);
  // doctor_image_author → author_image
  const [selectedFileAuthor, setSelectedFileAuthor] = useState(null);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [selectedFaqs, setSelectedFaqs] = useState([]);
  const [errors, setErrors] = useState({});
  const [descError, setDescError] = useState("");

  // ─── Fetch all blogs + keywords + categories + faqs ─────────────────────
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("flag", "1");
      fd.append("call_id", "");

      const response = await server_post_data(get_all_blogs, fd);

      if (!response.data.error) {
        const msg = response.data.message;
        setBlogs(msg.data_blog || []);
        setKeywords(msg.data_blog_keyword || []);
        setCategories(msg.data_blog_category || []);
        setAllFaqs(msg.data_blog_faq || []);
        setBlogImageBase(msg.blog_image || "");
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Field helper ─────────────────────────────────────────────────────────
  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // ─── Save / Update ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = {};
    if (!form.title_name.trim()) e.title_name = "Title is required";
    if (!form.tag_line.trim()) e.tag_line = "Introduction is required";
    if (!form.author.trim()) e.author = "Author is required";
    if (!form.read_minit) e.read_minit = "Read time is required";
    if (!form.page_url_data.trim()) e.page_url_data = "Page URL is required";
    if (!form.keyword_id) e.keyword_id = "Keyword is required";
    setErrors(e);

    const plainText = editorData.replace(/<[^>]*>/g, "").trim();
    if (!plainText) {
      setDescError("Description 1 is required");
      return;
    } else {
      setDescError("");
    }
    if (Object.keys(e).length > 0) return;

    try {
      setSaveLoader(true);

      const fd = new FormData();
      fd.append("main_id", form.primary_id);
      fd.append("admin_id", "1"); // replace with actual logged-in admin id
      fd.append("title_data", form.title_name);
      fd.append("tagline_data", form.tag_line);
      fd.append("author_data", form.author);
      fd.append("author_position", form.author_position);
      fd.append("author_bio", form.author_bio);
      fd.append("read_minit", form.read_minit);
      fd.append("page_url_data", form.page_url_data);
      fd.append("description_data", editorData);
      fd.append("description_data2", editorData2 || "null");
      fd.append("description_data3", editorData3 || "null");
      fd.append("selectfaqdata", JSON.stringify(selectedFaqs));
      fd.append("category_id", form.category_id || "0");

      // keyword — format: "id~@~keyword" or "other"
      if (isOtherSelected) {
        fd.append("keyword", "other");
        fd.append("otherKeyword", form.otherKeyword);
      } else {
        fd.append("keyword", form.keyword_id);
      }

      // existing image URLs (used as fallback if no new file uploaded)
      fd.append("old_image_blog", form.image_url || "");
      fd.append("old_image_blog1", form.image_url1 || "");
      fd.append("old_image_blog2", form.image_url2 || "");
      fd.append("old_image_blog3", form.image_url3 || "");
      fd.append("old_image_author", form.author_image || "");

      // new file uploads
      if (selectedFileBlog) fd.append("doctor_image_blog1", selectedFileBlog);
      if (selectedFileBlog2) fd.append("doctor_image_blog", selectedFileBlog2);
      if (selectedFileBlog4) fd.append("doctor_image_blog2", selectedFileBlog4);
      if (selectedFileBlog5) fd.append("doctor_image_blog3", selectedFileBlog5);
      if (selectedFileAuthor)
        fd.append("doctor_image_author", selectedFileAuthor);

      const response = await server_post_data(save_update_blog, fd);

      if (!response.data.error) {
        toast({ title: "Success", description: response.data.message });
        resetForm();
        setFormVisible(false);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoader(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoader(true);

      const fd = new FormData();
      fd.append("id_for_delete", deleteTarget.primary_id);
      fd.append("flag_for", "1"); // AdminBlog in FLAG_MODEL_MAP
      fd.append("for_status_final", "d");

      const response = await server_post_data(delete_website_master_data, fd);

      if (!response.data.error) {
        toast({ title: "Deleted", description: response.data.message });
        setDeleteTarget(null);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ─── Edit ────────────────────────────────────────────────────────────────
  const handleEdit = async (blog) => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("flag", "3");
      fd.append("call_id", blog.primary_id);

      const response = await server_post_data(get_all_blogs, fd);

      if (!response.data.error) {
        const msg = response.data.message;
        const data = msg.data_blog?.[0] || blog;
        const faqIds = msg.data_blog_faq_id || [];
        const faqs = msg.data_blog_faq || allFaqs;

        // Keep categories fresh if returned
        if (msg.data_blog_category?.length) {
          setCategories(msg.data_blog_category);
        }

        setForm({
          primary_id: data.primary_id,
          title_name: data.title_name || "",
          tag_line: data.tag_line || "",
          author: data.author || "",
          author_position: data.author_position || "",
          author_bio: data.author_bio || "",
          author_image: data.author_image || "",
          read_minit: data.read_minit || "",
          page_url_data: data.page_url_data || "",
          keyword_id: data.keyword_id
            ? `${data.keyword_id}~@~${data.keyword}`
            : "",
          otherKeyword: "",
          category_id: data.category_id ? String(data.category_id) : "0",
          image_url: data.image_url || "",
          image_url1: data.image_url1 || "",
          image_url2: data.image_url2 || "",
          image_url3: data.image_url3 || "",
        });

        setEditorData(data.description || "");
        setEditorData2(data.description2 || "");
        setEditorData3(data.description3 || "");
        setAllFaqs(faqs);

        const linkedFaqIds = faqIds.map((f) => f.faq_id);
        setSelectedFaqs(
          faqs.filter((f) => linkedFaqIds.includes(f.primary_id)),
        );
      }
    } catch (error) {
      console.error(error);
      // Fallback to local data
      setForm({
        primary_id: blog.primary_id,
        title_name: blog.title_name || "",
        tag_line: blog.tag_line || "",
        author: blog.author || "",
        author_position: blog.author_position || "",
        author_bio: blog.author_bio || "",
        author_image: blog.author_image || "",
        read_minit: blog.read_minit || "",
        page_url_data: blog.page_url_data || "",
        keyword_id: blog.keyword_id
          ? `${blog.keyword_id}~@~${blog.keyword}`
          : "",
        otherKeyword: "",
        category_id: blog.category_id ? String(blog.category_id) : "0",
        image_url: blog.image_url || "",
        image_url1: blog.image_url1 || "",
        image_url2: blog.image_url2 || "",
        image_url3: blog.image_url3 || "",
      });
      setEditorData(blog.description || "");
      setEditorData2(blog.description2 || "");
      setEditorData3(blog.description3 || "");
      setSelectedFaqs([]);
    } finally {
      setFetchLoader(false);
    }

    setIsOtherSelected(false);
    setErrors({});
    setDescError("");
    setSelectedFileBlog(null);
    setSelectedFileBlog2(null);
    setSelectedFileBlog4(null);
    setSelectedFileBlog5(null);
    setSelectedFileAuthor(null);
    setFormVisible(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── Reset form ───────────────────────────────────────────────────────────
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditorData("");
    setEditorData2("");
    setEditorData3("");
    setSelectedFileBlog(null);
    setSelectedFileBlog2(null);
    setSelectedFileBlog4(null);
    setSelectedFileBlog5(null);
    setSelectedFileAuthor(null);
    setIsOtherSelected(false);
    setSelectedFaqs([]);
    setErrors({});
    setDescError("");
  };

  // ─── Keyword change ───────────────────────────────────────────────────────
  const handleKeywordChange = (e) => {
    const val = e.target.value;
    if (val === "other") {
      setIsOtherSelected(true);
      setForm((prev) => ({ ...prev, keyword_id: "other", otherKeyword: "" }));
    } else {
      setIsOtherSelected(false);
      setForm((prev) => ({ ...prev, keyword_id: val, otherKeyword: "" }));
    }
    if (errors.keyword_id) setErrors((prev) => ({ ...prev, keyword_id: null }));
  };

  // ─── FAQ toggle ───────────────────────────────────────────────────────────
  const toggleFaq = (faq) => {
    setSelectedFaqs((prev) => {
      const exists = prev.find((f) => f.primary_id === faq.primary_id);
      return exists
        ? prev.filter((f) => f.primary_id !== faq.primary_id)
        : [...prev, faq];
    });
  };

  // ─── Filter + Pagination ──────────────────────────────────────────────────
  const filtered = blogs.filter((b) =>
    [b.title_name, b.author, b.tag_line]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // ─── Category name lookup helper ──────────────────────────────────────────
  const getCategoryName = (categoryId) => {
    if (!categoryId || categoryId === "0") return null;
    const cat = categories.find(
      (c) => String(c.primary_id) === String(categoryId),
    );
    return cat?.category_name || null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (!formVisible) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Blog Posts</h2>
            <p className="text-sm text-muted-foreground">
              Create and manage your blog content.
            </p>
          </div>
          <Button
            className="gap-2 self-start rounded-xl"
            onClick={() => {
              resetForm();
              setFormVisible(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add Blog Post
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <Card>
          {fetchLoader && (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          )}

          {!fetchLoader && paginated.length === 0 && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <BookOpen className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No blog posts found
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                {search
                  ? "Try a different search term."
                  : "Add your first blog post to get started."}
              </p>
              {!search && (
                <Button
                  size="sm"
                  className="gap-2 rounded-xl"
                  onClick={() => {
                    resetForm();
                    setFormVisible(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> Add Blog Post
                </Button>
              )}
            </CardContent>
          )}

          {!fetchLoader && paginated.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Date
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:table-cell">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Title
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">
                        Category
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:table-cell">
                        Author
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((blog, index) => (
                      <tr
                        key={blog.primary_id}
                        className="border-b border-border transition-colors hover:bg-muted/30"
                      >
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <p className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDate(blog.entry_date)}
                          </p>
                        </td>
                        <td className="hidden px-6 py-4 sm:table-cell">
                          <div className="h-12 w-16 overflow-hidden rounded-lg border border-border bg-muted">
                            {blog.image_url1 || blog.image_url ? (
                              <img
                                src={blog.image_url1 || blog.image_url}
                                alt={blog.title_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <ImagePlus className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="max-w-[180px] px-6 py-4">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {blog.title_name}
                          </p>
                          <Badge variant="outline" className="mt-1 text-[10px]">
                            {blog.read_minit} min read
                          </Badge>
                        </td>
                        <td className="hidden max-w-[160px] px-6 py-4 md:table-cell">
                          {getCategoryName(blog.category_id) ? (
                            <Badge variant="secondary" className="text-[10px]">
                              {getCategoryName(blog.category_id)}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </td>
                        <td className="hidden px-6 py-4 lg:table-cell">
                          <p className="text-sm text-muted-foreground">
                            {blog.author}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Edit"
                              onClick={() => handleEdit(blog)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Delete"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setDeleteTarget(blog)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-border px-6 py-4">
                <p className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{" "}
                  {filtered.length} blogs
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .map((p, i, arr) => (
                      <span key={p} className="flex items-center gap-1">
                        {i > 0 && arr[i - 1] !== p - 1 && (
                          <span className="px-1 text-xs text-muted-foreground">
                            …
                          </span>
                        )}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium transition-all ${
                            currentPage === p
                              ? "bg-gradient-primary text-white shadow-glow"
                              : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                          }`}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Delete Modal */}
        {deleteTarget && (
          <DeleteModal
            blog={deleteTarget}
            onConfirm={handleDeleteConfirm}
            onClose={() => setDeleteTarget(null)}
            deleteLoader={deleteLoader}
          />
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FORM VIEW
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {form.primary_id !== "0" ? "Edit Blog Post" : "Add New Blog Post"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {form.primary_id !== "0"
              ? "Update the blog post details below."
              : "Fill in the details to publish a new blog post."}
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2 rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to List
        </Button>
      </div>

      {/* Error banner */}
      {(Object.values(errors).some(Boolean) || descError) && (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-sm font-semibold text-destructive">
            Please fix the errors below before saving.
          </p>
        </div>
      )}

      {/* Images */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Blog Images</h3>
          <p className="text-xs text-muted-foreground">
            Upload the card thumbnail and main hero image
          </p>
        </div>
        <CardContent className="p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            {/* doctor_image_blog1 → image_url1 (card thumbnail) */}
            <ImageUploadField
              id="doctor_image_blog1"
              name="doctor_image_blog1"
              label="Blog Card Image"
              hint="Recommended size: 400 × 250 px"
              file={selectedFileBlog}
              onChange={(e) => setSelectedFileBlog(e.target.files[0] || null)}
              oldUrl={form.image_url1}
            />
            {/* doctor_image_blog → image_url (hero) */}
            <ImageUploadField
              id="doctor_image_blog"
              name="doctor_image_blog"
              label="Main Blog Hero Image"
              hint="Recommended size: 1400 × 430 px"
              file={selectedFileBlog2}
              onChange={(e) => setSelectedFileBlog2(e.target.files[0] || null)}
              oldUrl={form.image_url}
            />
          </div>
        </CardContent>
      </Card>

      {/* Blog Details */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Blog Details</h3>
          <p className="text-xs text-muted-foreground">
            Category, author info, read time, URL and keyword
          </p>
        </div>
        <CardContent className="space-y-4 p-6">
          {/* Row 1: Category + Read Time */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* category_id */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Category
              </label>
              <div className="relative">
                <select
                  value={form.category_id}
                  onChange={(e) => setField("category_id", e.target.value)}
                  className="flex h-9 w-full appearance-none rounded-lg border border-input bg-background px-3 py-1 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="0">Select Category (optional)</option>
                  {categories.map((cat) => (
                    <option key={cat.primary_id} value={String(cat.primary_id)}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
                <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground" />
              </div>
            </div>

            {/* Read Time */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Read Time (minutes) <span className="text-destructive">*</span>
              </label>
              <Input
                type="number"
                placeholder="e.g. 5"
                value={form.read_minit}
                onChange={(e) =>
                  setField("read_minit", e.target.value.replace(/\D/g, ""))
                }
                className={errors.read_minit ? "border-destructive" : ""}
              />
              {errors.read_minit && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.read_minit}
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Author + Author Position + Page URL */}
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Author */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Author <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g. Advance Collision Team"
                value={form.author}
                onChange={(e) => setField("author", e.target.value)}
                className={errors.author ? "border-destructive" : ""}
              />
              {errors.author && (
                <p className="mt-1 text-xs text-destructive">{errors.author}</p>
              )}
            </div>

            {/* Author Position */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Author Position
              </label>
              <Input
                placeholder="e.g. Senior Technician"
                value={form.author_position}
                onChange={(e) => setField("author_position", e.target.value)}
              />
            </div>

            {/* Page URL */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Page URL Slug <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g. what-to-do-after-car-accident"
                value={form.page_url_data}
                onChange={(e) => setField("page_url_data", e.target.value)}
                className={errors.page_url_data ? "border-destructive" : ""}
              />
              {errors.page_url_data && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.page_url_data}
                </p>
              )}
            </div>
          </div>

          {/* Row 3: Author Bio + Keyword */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Author Bio */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Author Bio
              </label>
              <textarea
                rows={2}
                placeholder="Short author bio..."
                value={form.author_bio}
                onChange={(e) => setField("author_bio", e.target.value)}
                className="flex min-h-[60px] w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {/* Keyword */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Keyword <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.keyword_id}
                  onChange={handleKeywordChange}
                  className={`flex h-9 w-full appearance-none rounded-lg border bg-background px-3 py-1 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    errors.keyword_id ? "border-destructive" : "border-input"
                  }`}
                >
                  <option value="" disabled>
                    Select Keyword
                  </option>
                  {keywords.map((kw) => (
                    <option
                      key={kw.primary_id}
                      value={`${kw.primary_id}~@~${kw.keyword}`}
                    >
                      {kw.keyword}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
                <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground" />
              </div>
              {errors.keyword_id && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.keyword_id}
                </p>
              )}
              {isOtherSelected && (
                <Input
                  className="mt-2"
                  placeholder="Enter custom keyword"
                  value={form.otherKeyword}
                  onChange={(e) => setField("otherKeyword", e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Row 4: Author Image */}
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageUploadField
              id="doctor_image_author"
              name="doctor_image_author"
              label="Author Image"
              hint="Recommended size: 200 × 200 px"
              file={selectedFileAuthor}
              onChange={(e) => setSelectedFileAuthor(e.target.files[0] || null)}
              oldUrl={form.author_image}
            />
          </div>
        </CardContent>
      </Card>

      {/* Title & Introduction */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">
            Title & Introduction
          </h3>
        </div>
        <CardContent className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Blog Title <span className="text-destructive">*</span>
            </label>
            <Input
              placeholder="Enter blog title..."
              value={form.title_name}
              onChange={(e) => setField("title_name", e.target.value)}
              className={errors.title_name ? "border-destructive" : ""}
            />
            {errors.title_name && (
              <p className="mt-1 text-xs text-destructive">
                {errors.title_name}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Introduction / Tagline <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={3}
              placeholder="Enter a short introduction..."
              value={form.tag_line}
              onChange={(e) => setField("tag_line", e.target.value)}
              className={`flex min-h-[80px] w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                errors.tag_line ? "border-destructive" : "border-input"
              }`}
            />
            {errors.tag_line && (
              <p className="mt-1 text-xs text-destructive">{errors.tag_line}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Description 1 */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">
            Description 1 <span className="text-destructive">*</span>
          </h3>
          <p className="text-xs text-muted-foreground">
            Main body content of the blog post
          </p>
        </div>
        <CardContent className="p-6">
          <RichEditor
            value={editorData}
            onChange={(val) => {
              setEditorData(val);
              if (descError) setDescError("");
            }}
            placeholder="Write the main blog content here..."
          />
          {descError && (
            <p className="mt-2 text-xs text-destructive">{descError}</p>
          )}
        </CardContent>
      </Card>

      {/* Image 2 + Description 2 */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">
            Image 2 & Description 2
          </h3>
          <p className="text-xs text-muted-foreground">
            Optional second section
          </p>
        </div>
        <CardContent className="space-y-5 p-6">
          <ImageUploadField
            id="doctor_image_blog2"
            name="doctor_image_blog2"
            label="Blog Image 2"
            hint="Recommended size: 400 × 250 px"
            file={selectedFileBlog4}
            onChange={(e) => setSelectedFileBlog4(e.target.files[0] || null)}
            oldUrl={form.image_url2}
          />
          <div>
            <label className="mb-2 block text-xs font-semibold text-foreground">
              Description 2
            </label>
            <RichEditor
              value={editorData2}
              onChange={setEditorData2}
              placeholder="Write section 2 content..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Image 3 + Description 3 */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">
            Image 3 & Description 3
          </h3>
          <p className="text-xs text-muted-foreground">
            Optional third section
          </p>
        </div>
        <CardContent className="space-y-5 p-6">
          <ImageUploadField
            id="doctor_image_blog3"
            name="doctor_image_blog3"
            label="Blog Image 3"
            hint="Recommended size: 400 × 250 px"
            file={selectedFileBlog5}
            onChange={(e) => setSelectedFileBlog5(e.target.files[0] || null)}
            oldUrl={form.image_url3}
          />
          <div>
            <label className="mb-2 block text-xs font-semibold text-foreground">
              Description 3
            </label>
            <RichEditor
              value={editorData3}
              onChange={setEditorData3}
              placeholder="Write section 3 content..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Related FAQs */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Related FAQs</h3>
          <p className="text-xs text-muted-foreground">
            Select FAQs to show at the bottom of this post
          </p>
        </div>
        <CardContent className="p-6">
          {selectedFaqs.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedFaqs.map((faq) => (
                <span
                  key={faq.primary_id}
                  className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                >
                  {faq.question_name}
                  <button
                    onClick={() => toggleFaq(faq)}
                    className="transition-colors hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {allFaqs.map((faq) => {
              const isSelected = selectedFaqs.some(
                (f) => f.primary_id === faq.primary_id,
              );
              return (
                <button
                  key={faq.primary_id}
                  onClick={() => toggleFaq(faq)}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-all ${
                    isSelected
                      ? "border-primary/30 bg-primary/5 text-primary"
                      : "border-border text-foreground hover:border-primary/20 hover:bg-accent"
                  }`}
                >
                  <div
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                      isSelected
                        ? "border-primary bg-primary"
                        : "border-muted-foreground"
                    }`}
                  >
                    {isSelected && (
                      <div className="h-2 w-2 rounded-full bg-white" />
                    )}
                  </div>
                  {faq.question_name}
                </button>
              );
            })}
            {allFaqs.length === 0 && (
              <p className="text-xs italic text-muted-foreground">
                No FAQs available. Add some in the FAQ Manager first.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          Cancel
        </Button>
        <Button
          className="gap-2 rounded-xl"
          disabled={saveLoader}
          onClick={handleSave}
        >
          {saveLoader ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {form.primary_id !== "0" ? "Save Changes" : "Publish Blog Post"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Blogs;
